import type { DlqMessageSummary, WorkflowRunDetailResponse } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumbs } from '@/components/breadcrumbs';
import { RedriveDialog } from '@/components/jobs/redrive-dialog';
import { EmptyState, ErrorBanner, LoadingCard } from '@/components/ui/state-views';
import { getJobDlq, getWorkflowRun } from '@/config/api-helpers';
import { recordVisit } from '@/hooks/useVisitHistory';
import { formatTimestamp } from '@/lib/utils';
import {
  formatDuration,
  JsonDetails,
  StatusBadge,
  WorkflowContextMetadata,
  WorkflowTimeline,
} from '@/pages/workflows/workflowRunUi';

const WORKFLOW_ENGINE_CANONICAL_JOB = 'workflowEngine';
const WORKFLOW_ENGINE_DLQ_SCAN_LIMIT = 200;
const START_WORKFLOW_JOB_NAME = 'startWorkflow';
const WORKFLOW_DLQ_MATCH_KEYS = [
  'applicationId',
  'credentialingRequestId',
  'licenseApplicationId',
  'npi',
  'providerEnrollmentId',
  'providerId',
  'providerNpi',
  'requestId',
];

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value != null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asComparablePrimitive(value: unknown): string | null {
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

function normalizeJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeJson);
  const record = asRecord(value);
  if (record == null) return value;

  return Object.fromEntries(
    Object.entries(record)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, normalizeJson(entry)])
  );
}

function getComparableJson(value: unknown): string | null {
  try {
    return JSON.stringify(normalizeJson(value));
  } catch {
    return null;
  }
}

function parseDlqMessageBody(message: DlqMessageSummary): Record<string, unknown> | null {
  try {
    return asRecord(JSON.parse(message.body));
  } catch {
    return null;
  }
}

function valueIncludesRunId(value: unknown, runId: string): boolean {
  if (typeof value === 'string') return value.includes(runId);
  if (Array.isArray(value)) return value.some((entry) => valueIncludesRunId(entry, runId));
  if (typeof value === 'object' && value != null) {
    return Object.values(value).some((entry) => valueIncludesRunId(entry, runId));
  }
  return false;
}

function dlqMessageMatchesRunId(
  message: DlqMessageSummary,
  parsedBody: Record<string, unknown> | null,
  runId: string
): boolean {
  if (parsedBody != null) {
    if (valueIncludesRunId(parsedBody, runId)) {
      return true;
    }
  } else {
    if (message.body.includes(runId)) {
      return true;
    }
  }

  return Object.values(message.messageAttributes).some((value) => value.includes(runId));
}

function recordsShareWorkflowIdentifier(
  messageRecord: Record<string, unknown> | null,
  runRecord: Record<string, unknown> | null
): boolean {
  if (messageRecord == null || runRecord == null) return false;

  return WORKFLOW_DLQ_MATCH_KEYS.some((key) => {
    const messageValue = asComparablePrimitive(messageRecord[key]);
    const runValue = asComparablePrimitive(runRecord[key]);
    return messageValue != null && runValue != null && messageValue === runValue;
  });
}

function startWorkflowMessageMatchesRun(
  parsedBody: Record<string, unknown>,
  detail: WorkflowRunDetailResponse
): boolean {
  const params = asRecord(parsedBody.params);
  if (params == null || asComparablePrimitive(params.workflowId) !== detail.run.workflowId) {
    return false;
  }

  const messageInput = params.input;
  const messageInputJson = getComparableJson(messageInput);
  const runInputJson = getComparableJson(detail.input);
  if (messageInputJson != null && runInputJson != null && messageInputJson === runInputJson) {
    return true;
  }

  const messageInputRecord = asRecord(messageInput);
  const runInputRecord = asRecord(detail.input);
  return (
    recordsShareWorkflowIdentifier(messageInputRecord, runInputRecord) ||
    recordsShareWorkflowIdentifier(messageInputRecord, detail.run.logContext)
  );
}

function getDlqMessageJobName(message: DlqMessageSummary, parsedBody: Record<string, unknown> | null): string {
  return asComparablePrimitive(parsedBody?.name) ?? asComparablePrimitive(parsedBody?.jobName) ?? message.jobName;
}

function dlqMessageMatchesRun(message: DlqMessageSummary, detail: WorkflowRunDetailResponse): boolean {
  const parsedBody = parseDlqMessageBody(message);
  if (dlqMessageMatchesRunId(message, parsedBody, detail.run.runId)) {
    return true;
  }

  const jobName = getDlqMessageJobName(message, parsedBody);
  return (
    jobName === START_WORKFLOW_JOB_NAME && parsedBody != null && startWorkflowMessageMatchesRun(parsedBody, detail)
  );
}

function getExplicitDlqMessageStepKey(
  message: DlqMessageSummary,
  parsedBody: Record<string, unknown> | null
): string | null {
  const params = asRecord(parsedBody?.params);
  const bodyContext = asRecord(parsedBody?.context);
  const paramsContext = asRecord(params?.context);

  return (
    asComparablePrimitive(params?.stepKey) ??
    asComparablePrimitive(parsedBody?.stepKey) ??
    asComparablePrimitive(bodyContext?.stepKey) ??
    asComparablePrimitive(paramsContext?.stepKey) ??
    asComparablePrimitive(message.messageAttributes.stepKey)
  );
}

function getDlqMessageStepKey(message: DlqMessageSummary, detail: WorkflowRunDetailResponse): string | null {
  const parsedBody = parseDlqMessageBody(message);
  const explicitStepKey = getExplicitDlqMessageStepKey(message, parsedBody);
  if (explicitStepKey != null && detail.steps.some((step) => step.stepKey === explicitStepKey)) {
    return explicitStepKey;
  }

  if (getDlqMessageJobName(message, parsedBody) === START_WORKFLOW_JOB_NAME) {
    return detail.steps[0]?.stepKey ?? null;
  }

  return null;
}

export function WorkflowRunPage() {
  const { runId = '' } = useParams();
  const [redriveDialogMessages, setRedriveDialogMessages] = useState<DlqMessageSummary[] | null>(null);

  const {
    data: detail,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['workflow-run', runId],
    queryFn: () => getWorkflowRun(runId, { days: 30 }),
    enabled: Boolean(runId),
  });

  const workflowEngineDlq = useQuery({
    queryKey: ['job-dlq', WORKFLOW_ENGINE_CANONICAL_JOB, WORKFLOW_ENGINE_DLQ_SCAN_LIMIT],
    queryFn: () => getJobDlq(WORKFLOW_ENGINE_CANONICAL_JOB, { limit: WORKFLOW_ENGINE_DLQ_SCAN_LIMIT }),
    enabled: detail != null,
  });

  const matchingDlqMessages = useMemo(() => {
    if (detail == null) return [];
    return (workflowEngineDlq.data?.messages ?? []).filter((message) => dlqMessageMatchesRun(message, detail));
  }, [detail, workflowEngineDlq.data?.messages]);

  const dlqMessagesByStepKey = useMemo(() => {
    const byStepKey = new Map<string, DlqMessageSummary[]>();
    if (detail == null) return byStepKey;

    for (const message of matchingDlqMessages) {
      const stepKey = getDlqMessageStepKey(message, detail);
      if (stepKey == null) continue;
      byStepKey.set(stepKey, [...(byStepKey.get(stepKey) ?? []), message]);
    }

    return byStepKey;
  }, [detail, matchingDlqMessages]);

  useEffect(() => {
    if (runId) {
      recordVisit(`/workflows/${encodeURIComponent(runId)}`, runId, 'workflow');
    }
  }, [runId]);

  if (!runId) {
    return <EmptyState message='Workflow run not found.' />;
  }

  if (isLoading) {
    return <LoadingCard message='Loading workflow run…' />;
  }

  if (error) {
    return <ErrorBanner message={error instanceof Error ? error.message : 'Failed to load workflow run'} />;
  }

  if (!detail) {
    return null;
  }

  return (
    <div className='space-y-5'>
      <Breadcrumbs items={[{ label: 'Workflows', to: '/workflows' }, { label: detail.run.workflowId }]} />

      <div className='overflow-hidden rounded border border-neutral-200 bg-white'>
        <div className='p-4'>
          <div className='flex flex-wrap items-start justify-between gap-4'>
            <div className='min-w-0'>
              <h2 className='font-mediumbold truncate text-xl text-neutral-900'>{detail.run.workflowId}</h2>
              <div className='mt-1 truncate text-sm text-neutral-500'>{detail.run.runId}</div>
            </div>
            <StatusBadge status={detail.run.status} />
          </div>

          <div className='mt-4 grid gap-x-8 gap-y-3 text-sm text-neutral-700 sm:grid-cols-2 lg:grid-cols-4'>
            <div className='min-w-0'>
              <div className='text-[11px] uppercase tracking-wide text-neutral-400'>Current step</div>
              <div className='mt-1 truncate font-medium text-neutral-900'>{detail.run.currentStepKey ?? '-'}</div>
            </div>
            <div>
              <div className='text-[11px] uppercase tracking-wide text-neutral-400'>Events</div>
              <div className='mt-1 font-medium text-neutral-900'>{detail.run.eventCount}</div>
            </div>
            <div>
              <div className='text-[11px] uppercase tracking-wide text-neutral-400'>Duration</div>
              <div className='mt-1 font-medium text-neutral-900'>{formatDuration(detail.run.durationMs)}</div>
            </div>
            <div>
              <div className='text-[11px] uppercase tracking-wide text-neutral-400'>Last event</div>
              <div className='mt-1 font-medium text-neutral-900'>{formatTimestamp(detail.run.lastEventAt)}</div>
            </div>
          </div>

          {detail.run.failureMessage && (
            <div className='mt-4 rounded border border-red-100 bg-red-50 p-3 text-sm text-red-700'>
              {detail.run.failureMessage}
            </div>
          )}
        </div>

        <div className='border-t border-neutral-100 bg-neutral-50 px-4 py-3'>
          <div className='mb-2 text-[11px] uppercase tracking-wide text-neutral-400'>Metadata</div>
          <WorkflowContextMetadata context={detail.run.logContext} />
        </div>
      </div>

      <JsonDetails defaultOpen title='Input' value={detail.input} />

      {workflowEngineDlq.error instanceof Error && (
        <ErrorBanner message={`Could not check WorkflowEngine DLQ messages: ${workflowEngineDlq.error.message}`} />
      )}

      <WorkflowTimeline
        dlqMessagesByStepKey={dlqMessagesByStepKey}
        isCheckingDlq={workflowEngineDlq.isLoading}
        onRedriveDlqMessages={setRedriveDialogMessages}
        timeline={detail.timeline}
      />

      {redriveDialogMessages != null && workflowEngineDlq.data != null && redriveDialogMessages.length > 0 && (
        <RedriveDialog
          mode='redrive'
          messages={redriveDialogMessages}
          dlqUrl={workflowEngineDlq.data.dlqUrl}
          canonicalJob={WORKFLOW_ENGINE_CANONICAL_JOB}
          smartRedriveInfo={workflowEngineDlq.data.smartRedriveInfo}
          onConfirm={() => {
            setRedriveDialogMessages(null);
            workflowEngineDlq.refetch();
          }}
          onCancel={() => setRedriveDialogMessages(null)}
        />
      )}
    </div>
  );
}
